import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Typography,
  notification,
} from 'antd';
import axios from 'axios';

import { stateOptions } from '@/config';
import useCancelEmailChange from '@/hooks/useCancelEmailChange';
import useResendEmailChangeConfirmationLink from '@/hooks/useResendEmailChangeConfirmationLink';
import useGlobalStore from '@/store/global';

const { Text } = Typography;
const { Option } = Select;

const ProviderEnrollmentForm = () => {
  const { themeColor, updateMeData, meData } = useGlobalStore();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const initialValuesRef = useRef<any>(null);
  const originalEmailRef = useRef<string>('');
  const hasUnconfirmedEmail = meData?.new_email;
  const { mutateAsync: resendEmailChangeConfirmationLink } = useResendEmailChangeConfirmationLink();
  const { mutateAsync: cancelEmailChange } = useCancelEmailChange();

  const loadData = async () => {
    const { data } = await axios.get('sessions/me');
    form.setFieldsValue(data);
    initialValuesRef.current = data;
    originalEmailRef.current = data.email;
    updateMeData(data);
  };

  let renderRef = useRef(false);

  useEffect(() => {
    if (renderRef.current) {
      renderRef.current = false;
      return;
    }
    loadData();
  }, []);

  const onFinish = async () => {
    const values = form.getFieldsValue();
    setLoading(true);
    try {
      const isNewEmail = originalEmailRef.current !== values.email;

      if (isNewEmail && hasUnconfirmedEmail) {
        notification.error({
          message: 'Change email failed',
          description: (
            <div>
              You have a pending change to <b>{meData?.new_email}</b>. Please confirm or cancel it
              before making another change.
            </div>
          ),
          duration: 0,
        });
        return;
      }

      await axios.post(
        'sessions/upgrade',
        {
          ...values,
          email: isNewEmail ? originalEmailRef.current : values.email,
          ...(isNewEmail && { new_email: values.email }),
        },
        {
          customError: true,
        }
      );
      await loadData();

      if (!isNewEmail) {
        navigate('/');
      }
    } catch (error: any) {
      if (error?.data?.message) {
        notification.error({
          message: 'Error updating profile',
          description: error.data.message,
          duration: 0,
        });
      }
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardChanges = () => {
    const currentValues = form.getFieldsValue();
    const initialValues = initialValuesRef.current;

    const hasChanges = Object.keys(currentValues).some((key) => {
      return currentValues[key] !== initialValues?.[key];
    });

    if (!hasChanges) {
      navigate('/');
      return;
    }

    Modal.confirm({
      title: 'Discard Changes',
      content: 'Are you sure you want to discard your changes?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        navigate('/');
      },
    });
  };

  const addressFields = [
    'reimbursement_address',
    'reimbursement_address_line2',
    'reimbursement_address_city',
    'reimbursement_address_state',
    'reimbursement_address_zip',
  ];

  const handleResendConfirmation = async () => {
    await resendEmailChangeConfirmationLink(undefined, {
      onSuccess: () => {
        notification.success({
          message: 'Confirmation link sent successfully.',
        });
      },
      onError: () => {
        notification.error({
          message: 'Failed to send confirmation link, please try again late.',
        });
      },
    });
  };

  const handleCancelEmailChange = async () => {
    await cancelEmailChange(undefined, {
      onSuccess: () => {
        notification.success({
          message: 'Email change cancelled successfully.',
        });
        loadData();
      },
      onError: () => {
        notification.error({
          message: 'Failed to cancel email change, please try again later.',
        });
      },
    });
  };

  // Rule for validating fields when any address field has a value
  const getAddressFieldRule = () => ({
    validator: async (_: any, value: any) => {
      // Get all form values
      const allValues = form.getFieldsValue();

      // Check if any address field has a value
      const hasAnyValue = addressFields.some(
        (field) =>
          allValues[field] !== undefined && allValues[field] !== null && allValues[field] !== ''
      );
      // If any field has a value and current field is empty, throw error
      if (hasAnyValue && (!value || value === '')) {
        throw new Error('This field is required when any address field is filled');
      }

      return Promise.resolve();
    },
  });

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-xl md:text-2xl font-bold" style={{ color: themeColor }}>
        Edit Provider Profile
      </h2>
      <hr className="my-2" />
      <Text>Providers make changes to your profile below and press "Save Changes".</Text>

      <div className="mt-6">
        {hasUnconfirmedEmail && (
          <Alert
            className="mb-5"
            message={
              <div className="space-y-2 p-2">
                <p>
                  We've sent a confirmation link to <b>{meData?.new_email}</b>. Please check your
                  inbox.
                </p>
                <div className="flex gap-4">
                  <Popconfirm
                    title="Are you sure you want to cancel the email change?"
                    onConfirm={handleCancelEmailChange}
                  >
                    <Button size="small" type="default" style={{ boxShadow: 'none' }}>
                      Cancel email change
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title="Are you sure you want to resend the confirmation link?"
                    onConfirm={handleResendConfirmation}
                  >
                    <Button
                      size="small"
                      type="primary"
                      style={{ backgroundColor: themeColor, boxShadow: 'none' }}
                    >
                      Resend confirmation link
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            }
            type="warning"
          />
        )}
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Row gutter={20}>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Your email"
                rules={[{ required: true, type: 'email' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="business_name" label="Business Name" rules={[{ required: true }]}>
                <Input placeholder="Financial reimbursement will be issued to this name" />
              </Form.Item>
            </Col>
          </Row>

          <div className="py-5">
            <h3 className="text-lg md:text-xl font-bold">SECTION 1: PHARMACY LOCATION</h3>
            <Text>
              THIS SECTION IS MANDATORY. IT REQUIRES THE ADDRESS AND SPECIFIC INFORMATION OF YOUR
              PHARMACY.
            </Text>
          </div>

          <Row gutter={20}>
            <Col xs={24} md={12}>
              <Form.Item name="setting" label="Location Setting" rules={[{ required: true }]}>
                <Select placeholder="Setting of this pharmacy location">
                  <Option value={1} key="1">
                    Physician Infusion Center
                  </Option>
                  <Option value={2} key="2">
                    Hospital Outpatient Facility
                  </Option>
                  <Option value={4} key="4">
                    Specialty Pharmacy (Home Infusion)
                  </Option>
                  <Option value={8} key="8">
                    Specialty Pharmacy
                  </Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="business_address"
                label="Location Address"
                rules={[{ required: true }]}
              >
                <Input placeholder="Location street address" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="business_address_line2" label="Location address Line 2">
                <Input placeholder="Location street address line 2" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="business_city" label="Location city" rules={[{ required: true }]}>
                <Input placeholder="Location city" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="business_state" label="Location State" rules={[{ required: true }]}>
                <Select placeholder="Location state">
                  {stateOptions.map((option) => (
                    <Option value={option.value} key={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="business_zip" label="Location ZIP">
                <Input placeholder="Location ZIP code (5-digits e.g. 91101)" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="business_phone" label="Location phone" rules={[{ required: true }]}>
                <Input placeholder="Location phone (including area code)" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="business_fax" label="Location fax" rules={[{ required: true }]}>
                <Input placeholder="Location fax number" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="point_of_contact"
                label="Location contact person name"
                rules={[{ required: true }]}
              >
                <Input placeholder="First and last name of the billing contact at this location" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="tax_id" label="Location tax ID" rules={[{ required: true }]}>
                <Input placeholder="Please provide the tax ID for this location" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="provider_npi" label="Provider NPI" rules={[{ required: true }]}>
                <Input placeholder="Please provide the 10-digit NPI number for this location" />
              </Form.Item>
            </Col>
          </Row>

          <div className="py-5">
            <h3 className="text-lg md:text-xl font-bold">
              SECTION 2: ALTERNATE FINANCIAL REIMBURSEMENT ADDRESS
            </h3>
            <Text>
              THIS SECTION IS OPTIONAL. IT IS ONLY REQUIRED IF YOUR PHARMACY LOCATION (ADDRESS
              LISTED ABOVE) WANTS TO RE-DIRECT COPAY ASSISTANCE REIMBURSEMENT TO A DIFFERENT
              ADDRESS. IF LEFT BLANK, REIMBURSEMENT WILL BE SENT TO THE ADDRESS LISTED IN SECTION 1
              ABOVE.
            </Text>
          </div>

          <Row gutter={20}>
            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_address"
                label="Financial reimbursement address"
                rules={[getAddressFieldRule()]}
                validateTrigger={['onChange', 'onBlur', 'onSubmit']}
              >
                <Input placeholder="Street address where financial reimbursement would be sent" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_address_line2"
                label="Financial reimbursement address line 2"
                rules={[getAddressFieldRule()]}
                validateTrigger={['onChange', 'onBlur', 'onSubmit']}
              >
                <Input placeholder="Financial reimbursement address line 2" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_address_city"
                label="Financial reimbursement city"
                rules={[getAddressFieldRule()]}
                validateTrigger={['onChange', 'onBlur', 'onSubmit']}
              >
                <Input placeholder="Financial reimbursement city" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_address_state"
                label="Financial reimbursement state"
                rules={[getAddressFieldRule()]}
                validateTrigger={['onChange', 'onBlur', 'onSubmit']}
              >
                <Select allowClear placeholder="Financial reimbursement state">
                  {stateOptions.map((option) => (
                    <Option value={option.value} key={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_address_zip"
                label="Financial reimbursement ZIP"
                rules={[getAddressFieldRule()]}
                validateTrigger={['onChange', 'onBlur', 'onSubmit']}
              >
                <Input placeholder="Financial reimbursement ZIP code (5-digits e.g. 91101)" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_contact_name"
                label="Financial reimbursement contact person name"
              >
                <Input placeholder="First and last name of the financial reimbursement contact" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_contact_phone"
                label="Financial reimbursement contact person phone"
              >
                <Input placeholder="Financial reimbursement contact direct phone (including area code)" />
              </Form.Item>
            </Col>
          </Row>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item className="mt-4 w-full">
              <Button
                onClick={handleDiscardChanges}
                style={{
                  width: '100%',
                  height: '40px',
                }}
              >
                Discard Changes
              </Button>
            </Form.Item>
            <Form.Item className="mt-4 w-full">
              <Button
                type="primary"
                htmlType="submit"
                disabled={loading}
                loading={loading}
                style={{
                  width: '100%',
                  backgroundColor: themeColor,
                  height: '40px',
                }}
              >
                Save Changes
              </Button>
            </Form.Item>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ProviderEnrollmentForm;
